/**
 * Chip — interactive chip with full a11y (real Button under the hood).
 *
 * Use when: the user can click the chip to toggle a filter, navigate, or
 * dismiss it. For non-interactive status/tag display use `Pill` (which is
 * presentational only). For numeric counts use `Counter`.
 *
 * Has proper `role="button"`, keyboard focus, hover/active states, and
 * `aria-pressed` for toggleable variants. Renders as a small Fluent `Button`
 * with chip-shaped styling (rounded, tint background).
 */
import type { ReactElement, ReactNode } from 'react';
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useLocale } from '../i18n/useLocale';

type ChipColor = 'brand' | 'success' | 'warning' | 'danger' | 'informative' | 'subtle';

const useStyles = makeStyles({
  root: {
    /* Chip-shaped: rounded corners, tint fill, semibold text */
    minHeight: '24px',
    height: '24px',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    maxWidth: '100%',
  },
  brand:        { backgroundColor: tokens.colorBrandBackground2,        color: tokens.colorBrandForeground1 },
  success:      { backgroundColor: tokens.colorPaletteGreenBackground1, color: tokens.colorPaletteGreenForeground1 },
  warning:      { backgroundColor: tokens.colorPaletteYellowBackground1, color: tokens.colorPaletteYellowForeground1 },
  danger:       { backgroundColor: tokens.colorPaletteRedBackground1,   color: tokens.colorPaletteRedForeground1 },
  informative:  { backgroundColor: tokens.colorNeutralBackground3,      color: tokens.colorNeutralForeground1 },
  subtle:       { backgroundColor: tokens.colorTransparentBackground,   color: tokens.colorNeutralForeground2 },
  /* Pressed/selected state — invert tone */
  pressed: {
    boxShadow: `inset 0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
});

interface ChipProps {
  children: ReactNode;
  color?: ChipColor;
  icon?: ReactElement;
  /** Selected/pressed state. When provided, chip becomes a toggle. */
  selected?: boolean;
  /** Show a dismiss (X) button on the right; calls `onDismiss`. */
  dismissible?: boolean;
  onDismiss?: () => void;
  onClick?: () => void;
  /** ARIA label for icon-only chips. */
  'aria-label'?: string;
  className?: string;
}

export function Chip({
  children,
  color = 'informative',
  icon,
  selected,
  dismissible,
  onDismiss,
  onClick,
  'aria-label': ariaLabel,
  className,
}: ChipProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const colorClass = styles[color];

  if (dismissible) {
    return (
      <span
        className={mergeClasses(styles.root, colorClass, selected && styles.pressed, className)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}
      >
        <Button
          appearance="transparent"
          size="small"
          icon={icon}
          onClick={onClick}
          aria-pressed={selected}
          aria-label={ariaLabel}
          style={{
            minHeight: 'unset',
            height: '20px',
            paddingLeft: 0,
            paddingRight: 0,
            color: 'inherit',
            fontWeight: 'inherit',
          }}
        >
          {children}
        </Button>
        <Button
          appearance="transparent"
          size="small"
          icon={<DismissRegular />}
          onClick={onDismiss}
          aria-label={t('common.aria.removeItem', { item: typeof children === 'string' ? children : t('common.item') })}
          style={{ minHeight: 'unset', height: '20px', color: 'inherit' }}
        />
      </span>
    );
  }

  return (
    <Button
      appearance="transparent"
      size="small"
      icon={icon}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={ariaLabel}
      className={mergeClasses(styles.root, colorClass, selected && styles.pressed, className)}
    >
      {children}
    </Button>
  );
}
